import { useMutation, useQuery } from "@apollo/client";
import {
  Alert, Card, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, IconButton, LinearProgress,
} from "@mui/material";
import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { UNSAFE_NavigationContext, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Loading from "components/Carteles/Loading";
import Footer from "components/Footer";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";
import { UserContext } from "context/user";
import { getUproColor } from "utils/colors";
import {
  CATEGORIAS_CONFIGURACION_EVENTO, CONFIGURACION_EVENTO, DIAGNOSTICO_CONFIGURACION_EVENTO,
  EXTENDER_FIN_EVENTO, GUARDAR_CONFIGURACION_EVENTO, OPCIONES_CONFIGURACION_EVENTO,
} from "services/configuracionEventos";
import PasoAutogestion from "./components/pasos/PasoAutogestion";
import PasoCobro from "./components/pasos/PasoCobro";
import PasoDatos from "./components/pasos/PasoDatos";
import PasoMenu from "./components/pasos/PasoMenu";
import PasoProductos from "./components/pasos/PasoProductos";
import PasoRevision from "./components/pasos/PasoRevision";
import {
  PASOS, PERMISO_CONFIGURAR_EVENTOS, RUTA_LISTADO, aInput, codigoErrorEvento, desdeConfiguracion, duplicarConfiguracion,
  eventoComenzado, eventoFinalizado, nuevoFormulario, problemaNuevoCierre, validarPaso, mensajeErrorEvento,
} from "./utils";

const INDICE_REVISION = PASOS.length - 1;

// react-router 6.3 con BrowserRouter no es un data router (no hay useBlocker):
// se bloquea con el history del router, que también intercepta el botón Atrás
// del navegador, los links del menú lateral y el cierre o recarga de la pestaña.
function useBloquearSalida(activo, alIntentarSalir) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const callback = useRef(alIntentarSalir);
  callback.current = alIntentarSalir;
  useEffect(() => {
    if (!activo || typeof navigator?.block !== "function") return undefined;
    const desbloquear = navigator.block((transicion) => {
      callback.current(() => { desbloquear(); transicion.retry(); });
    });
    return desbloquear;
  }, [activo, navigator]);
}

function ListaPasos({ paso, visitados, completos, irAPaso }) {
  return (
    <SoftBox component="nav" aria-label="Pasos del evento" display="flex" flexDirection="column" gap={0.5}>
      {PASOS.map((item, index) => {
        const activo = index === paso;
        const completo = completos.has(index) && !activo;
        const habilitado = visitados.has(index);
        return (
          <SoftBox key={item.id} component="button" type="button" disabled={!habilitado} aria-current={activo ? "step" : undefined}
            onClick={() => habilitado && irAPaso(index)}
            display="flex" alignItems="center" gap={1.5} p={1.25} borderRadius="lg"
            sx={{
              border: 0, textAlign: "left", font: "inherit", width: "100%",
              cursor: habilitado ? "pointer" : "default",
              opacity: habilitado ? 1 : 0.5,
              backgroundColor: activo ? "rgba(44, 48, 135, 0.08)" : "transparent",
              "&:hover": habilitado && !activo ? { backgroundColor: "rgba(52, 71, 103, 0.04)" } : {},
            }}>
            <SoftBox width="28px" height="28px" borderRadius="50%" display="flex" alignItems="center" justifyContent="center" flexShrink={0}
              sx={{
                color: "#fff",
                backgroundColor: completo ? getUproColor("uproGreen") : activo ? getUproColor("uproBlue") : "#ced4da",
              }}>
              {completo
                ? <Icon fontSize="small">check</Icon>
                : <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#fff" }}>{index + 1}</SoftTypography>}
            </SoftBox>
            <SoftBox minWidth={0}>
              <SoftTypography variant="button" fontWeight="bold" display="block" color={activo ? "dark" : "text"}>{item.titulo}</SoftTypography>
              <SoftTypography variant="caption" color="secondary">{item.ayuda}</SoftTypography>
            </SoftBox>
          </SoftBox>
        );
      })}
    </SoftBox>
  );
}

ListaPasos.propTypes = {
  paso: PropTypes.number.isRequired,
  visitados: PropTypes.instanceOf(Set).isRequired,
  completos: PropTypes.instanceOf(Set).isRequired,
  irAPaso: PropTypes.func.isRequired,
};

function Asistente({ inicial, opciones, categorias, titulo, soloLectura, restricciones, onRecargar }) {
  const navigate = useNavigate();
  const { handleSnackbar } = useContext(MessageManager);
  const { confirmar } = useContext(ConfirmarContext);
  const [form, setForm] = useState(inicial);
  const [paso, setPaso] = useState(0);
  // Al editar se puede saltar a cualquier paso; al crear se recorren en orden.
  const [visitados, setVisitados] = useState(() => new Set(inicial.id ? PASOS.map((_, index) => index) : [0]));
  const [errores, setErrores] = useState({});
  const [errorGuardar, setErrorGuardar] = useState("");
  const [desactualizado, setDesactualizado] = useState(false);
  const [guardar, { loading: guardando }] = useMutation(GUARDAR_CONFIGURACION_EVENTO);
  // Extender el cierre de un finalizado: solo el Fin queda editable.
  const [extendiendo, setExtendiendo] = useState(false);
  const [preguntarActivar, setPreguntarActivar] = useState(false);
  const [extenderFin, { loading: extendiendoFin }] = useMutation(EXTENDER_FIN_EVENTO);
  const sucio = useMemo(() => JSON.stringify(form) !== JSON.stringify(inicial), [form, inicial]);
  const contextoValidacion = { opciones, restricciones };
  const validar = (index) => (soloLectura ? {} : validarPaso(PASOS[index].id, form, contextoValidacion));

  // Cualquier salida con cambios sin guardar (menú, Atrás, cerrar la pestaña)
  // pide confirmación. Tras guardar se sale sin preguntar.
  const salidaPermitida = useRef(false);
  useBloquearSalida(sucio, (permitir) => {
    if (salidaPermitida.current) { permitir(); return; }
    confirmar({
      icon: "warning",
      title: "Salir sin guardar",
      message: "Los cambios que hizo se van a perder.",
      confirmLabel: "Salir",
      func: permitir,
    });
  });

  const setField = (campo, valor) => {
    if (soloLectura && !(extendiendo && campo === "fin")) return;
    setForm((prev) => ({ ...prev, [campo]: valor }));
    setErrores((prev) => {
      if (!prev[campo]) return prev;
      const resto = { ...prev };
      delete resto[campo];
      return resto;
    });
  };

  const diagnostico = useQuery(DIAGNOSTICO_CONFIGURACION_EVENTO, {
    variables: {
      input: {
        id: form.id || null,
        inicio: form.inicio,
        fin: form.fin,
        activo: form.activo,
        menuUniversitario: form.menuUniversitario,
        idsProductos: form.productos.filter((item) => item.habilitado).map((item) => String(item.idProducto)),
      },
    },
    skip: paso !== INDICE_REVISION || soloLectura,
    fetchPolicy: "network-only",
  });

  const completos = new Set(PASOS.map((item, index) => index)
    .filter((index) => visitados.has(index) && !Object.keys(validar(index)).length));

  const irAPaso = (destino) => {
    // Avanzar exige que los pasos anteriores sean válidos (en lectura se recorre libre).
    for (let index = 0; !soloLectura && index < destino; index += 1) {
      const encontrados = validar(index);
      if (Object.keys(encontrados).length) {
        setErrores(encontrados);
        setPaso(index);
        return;
      }
    }
    setErrores({});
    setPaso(destino);
    setVisitados((prev) => new Set([...prev, destino]));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Con cambios, el bloqueo de navegación pide la confirmación.
  const volver = () => navigate(RUTA_LISTADO);
  const duplicar = () => navigate(`${RUTA_LISTADO}/nuevo?duplicar=${form.id}`);

  const submit = async () => {
    setErrorGuardar("");
    setDesactualizado(false);
    for (let index = 0; index < INDICE_REVISION; index += 1) {
      const encontrados = validar(index);
      if (Object.keys(encontrados).length) { setErrores(encontrados); setPaso(index); return; }
    }
    try {
      const { data } = await guardar({ variables: { input: aInput(form) } });
      handleSnackbar(`Evento «${data.guardarConfiguracionEventoComedor.evento.nombre}» guardado`, "success");
      salidaPermitida.current = true;
      navigate(RUTA_LISTADO);
    } catch (e) {
      setDesactualizado(codigoErrorEvento(e) === "EVENTO_COMEDOR_MODIFICADO");
      setErrorGuardar(mensajeErrorEvento(e, "No se pudo guardar el evento."));
    }
  };

  const iniciarExtension = () => {
    setExtendiendo(true);
    setErrores({});
    setErrorGuardar("");
    setPaso(0);
  };

  const cancelarExtension = () => {
    setExtendiendo(false);
    setPreguntarActivar(false);
    setErrores({});
    setErrorGuardar("");
    setForm((prev) => ({ ...prev, fin: inicial.fin }));
  };

  const guardarExtension = async (activar) => {
    setPreguntarActivar(false);
    setErrorGuardar("");
    try {
      await extenderFin({ variables: { id: String(form.id), fin: form.fin, activar } });
    } catch (e) {
      setErrorGuardar(mensajeErrorEvento(e, "No se pudo extender el cierre del evento."));
      return;
    }
    handleSnackbar(`Cierre de «${form.nombre}» extendido`, "success");
    // La configuración recargada ya no está finalizada: el asistente vuelve a
    // armarse en edición normal (con las reglas de en curso o con ventas).
    onRecargar();
  };

  const confirmarExtension = () => {
    const problema = problemaNuevoCierre(form.fin);
    if (problema) { setErrores({ fin: problema }); setPaso(0); return; }
    if (!form.activo) { setPreguntarActivar(true); return; }
    guardarExtension(null);
  };

  // Recargar descarta lo editado y trae la versión vigente del evento.
  const recargar = () => confirmar({
    icon: "refresh",
    title: "Recargar el evento",
    message: "Se descartan sus cambios y se muestra la versión guardada por el otro usuario.",
    confirmLabel: "Recargar",
    func: () => { salidaPermitida.current = true; onRecargar(); },
  });

  const hayErrorBloqueante = Boolean(diagnostico.data?.diagnosticoConfiguracionEventoComedor?.eventosSolapados?.length);
  const pasoActual = PASOS[paso];
  const contenido = {
    datos: (
      <PasoDatos form={form} setField={setField} errores={errores} soloLectura={soloLectura}
        inicioBloqueado={Boolean(restricciones.inicioBloqueado)} finEditable={extendiendo} />
    ),
    productos: (
      <PasoProductos form={form} setField={setField} errores={errores} categorias={categorias}
        idsConVentas={restricciones.idsConVentas} soloLectura={soloLectura} />
    ),
    menu: <PasoMenu form={form} setField={setField} errores={errores} soloLectura={soloLectura} />,
    cobro: <PasoCobro form={form} setField={setField} errores={errores} opciones={opciones} soloLectura={soloLectura} />,
    autogestion: <PasoAutogestion form={form} setField={setField} errores={errores} soloLectura={soloLectura} />,
    revision: (
      <PasoRevision form={form} opciones={opciones} irAPaso={irAPaso} validar={validar} soloLectura={soloLectura}
        diagnostico={{ data: diagnostico.data?.diagnosticoConfiguracionEventoComedor, loading: diagnostico.loading, error: diagnostico.error }} />
    ),
  }[pasoActual.id];

  return (
    <Card>
      <SoftBox p={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <SoftBox display="flex" alignItems="center" gap={1}>
          <IconButton aria-label="Volver al listado" onClick={volver} sx={{ color: getUproColor("uproGreen") }}><Icon>arrow_back</Icon></IconButton>
          <SoftTypography variant="h6" sx={{ overflowWrap: "anywhere", minWidth: 0 }}>{titulo}</SoftTypography>
        </SoftBox>
        <SoftTypography variant="caption" color="secondary">Paso {paso + 1} de {PASOS.length}</SoftTypography>
      </SoftBox>
      <SoftBox display={{ xs: "block", md: "none" }} px={3}>
        <LinearProgress variant="determinate" value={((paso + 1) / PASOS.length) * 100} />
      </SoftBox>
      <Grid container>
        <Grid item md={3} display={{ xs: "none", md: "block" }}>
          <SoftBox px={2} pb={3} sx={{ position: "sticky", top: 96 }}>
            <ListaPasos paso={paso} visitados={visitados} completos={completos} irAPaso={irAPaso} />
          </SoftBox>
        </Grid>
        <Grid item xs={12} md={9}>
          <SoftBox px={{ xs: 2, sm: 3 }} pb={3} sx={{ borderLeft: { md: "1px solid #e9ecef" }, minWidth: 0, overflowWrap: "anywhere" }}>
            <SoftTypography variant="h5" fontWeight="bold">{pasoActual.titulo}</SoftTypography>
            <SoftTypography variant="caption" color="secondary" display="block" mb={2.5}>{pasoActual.ayuda}</SoftTypography>
            {soloLectura && !extendiendo && (
              <Alert severity="info" sx={{ mb: 2 }}
                action={(
                  <SoftBox display="flex" flexWrap="wrap" gap={0.5}>
                    <SoftButton size="small" color="info" variant="text" onClick={iniciarExtension}>Extender cierre</SoftButton>
                    <SoftButton size="small" color="info" variant="text" onClick={duplicar}>Duplicar</SoftButton>
                  </SoftBox>
                )}>
                El evento ya finalizó: se muestra solo para consulta. Para repetirlo, duplíquelo; para seguir vendiendo, extienda su cierre.
              </Alert>
            )}
            {extendiendo && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Indique el nuevo cierre en «Fin» (posterior a ahora, sin límite de duración). El resto del evento no cambia.
              </Alert>
            )}
            {!soloLectura && form.id && paso === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>Los cambios se aplican a las ventas siguientes; las ya registradas conservan sus precios.</Alert>
            )}
            {/* En lectura el fieldset deshabilita los controles nativos; setField además ignora cambios. */}
            <SoftBox component="fieldset" disabled={soloLectura && !extendiendo} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
              {contenido}
            </SoftBox>
            {errorGuardar && (
              <Alert severity="error" sx={{ mt: 2 }}
                action={desactualizado ? <SoftButton size="small" color="error" variant="text" onClick={recargar}>Recargar</SoftButton> : undefined}>
                {errorGuardar}
              </Alert>
            )}
            {extendiendo ? (
              <SoftBox display="flex" flexWrap="wrap" justifyContent="flex-end" alignItems="center" gap={1} mt={4}>
                <SoftButton variant="outlined" color="dark" circular onClick={cancelarExtension} disabled={extendiendoFin}>Cancelar</SoftButton>
                <SoftButton color="primary" circular onClick={confirmarExtension} disabled={extendiendoFin}>
                  <Icon>event_available</Icon>&nbsp;{extendiendoFin ? "Guardando…" : "Guardar nuevo cierre"}
                </SoftButton>
              </SoftBox>
            ) : (
            <SoftBox display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={2} mt={4}>
              <SoftButton variant="outlined" color="dark" circular onClick={volver} disabled={guardando}>{soloLectura ? "Volver" : "Cancelar"}</SoftButton>
              <SoftBox display="flex" flexWrap="wrap" gap={1}>
                {paso > 0 && (
                  <SoftButton variant="outlined" color="primary" circular onClick={() => irAPaso(paso - 1)} disabled={guardando}>
                    <Icon>arrow_back</Icon>&nbsp;Anterior
                  </SoftButton>
                )}
                {paso < INDICE_REVISION ? (
                  <SoftButton color="primary" circular onClick={() => irAPaso(paso + 1)}>
                    Siguiente&nbsp;<Icon>arrow_forward</Icon>
                  </SoftButton>
                ) : soloLectura ? (
                  <SoftButton color="primary" circular onClick={duplicar}>
                    <Icon>content_copy</Icon>&nbsp;Duplicar
                  </SoftButton>
                ) : (
                  <SoftButton color="primary" circular onClick={submit} disabled={guardando || diagnostico.loading || hayErrorBloqueante}>
                    <Icon>save</Icon>&nbsp;{guardando ? "Guardando…" : form.id ? "Guardar cambios" : "Crear evento"}
                  </SoftButton>
                )}
              </SoftBox>
            </SoftBox>
            )}
          </SoftBox>
        </Grid>
      </Grid>
      <Dialog open={preguntarActivar} onClose={() => setPreguntarActivar(false)} fullWidth maxWidth="sm"
        aria-labelledby="extender-inactivo-titulo">
        <DialogTitle id="extender-inactivo-titulo">El evento está inactivo</DialogTitle>
        <DialogContent>
          <SoftTypography variant="button" component="p">
            Si lo deja inactivo, el nuevo cierre se guarda pero el punto de venta no vende ni se aplican los bloqueos de la
            app hasta que lo active.
          </SoftTypography>
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
          <SoftButton variant="outlined" color="dark" circular onClick={() => setPreguntarActivar(false)}>Cancelar</SoftButton>
          <SoftButton variant="outlined" color="primary" circular onClick={() => guardarExtension(false)}>Extender y mantener inactivo</SoftButton>
          <SoftButton color="primary" circular onClick={() => guardarExtension(true)}>Extender y activar</SoftButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

Asistente.propTypes = {
  inicial: PropTypes.object.isRequired,
  opciones: PropTypes.object,
  categorias: PropTypes.array.isRequired,
  titulo: PropTypes.string.isRequired,
  soloLectura: PropTypes.bool,
  restricciones: PropTypes.shape({
    inicioBloqueado: PropTypes.bool,
    ultimaVenta: PropTypes.string,
    idsConVentas: PropTypes.arrayOf(PropTypes.string),
    comenzado: PropTypes.bool,
    finGuardado: PropTypes.string,
  }),
  onRecargar: PropTypes.func,
};

Asistente.defaultProps = {
  soloLectura: false,
  restricciones: {},
  onRecargar: () => {},
};

// Pantalla de alta, edición (/configuracion-eventos/:id) y duplicado
// (/configuracion-eventos/nuevo?duplicar=:id) de un evento.
export default function EditorEvento() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const idDuplicar = searchParams.get("duplicar");
  const idCargar = id || idDuplicar;
  const { verificarAcceso } = useContext(UserContext);
  const autorizado = verificarAcceso(PERMISO_CONFIGURAR_EVENTOS);

  const opciones = useQuery(OPCIONES_CONFIGURACION_EVENTO, { skip: !autorizado });
  const categorias = useQuery(CATEGORIAS_CONFIGURACION_EVENTO, { skip: !autorizado });
  const config = useQuery(CONFIGURACION_EVENTO, {
    variables: { id: String(idCargar) },
    skip: !autorizado || !idCargar,
    fetchPolicy: "network-only",
  });

  const configuracion = config.data?.configuracionEventoComedor;
  const inicial = useMemo(() => {
    if (!idCargar) return nuevoFormulario();
    if (!configuracion) return null;
    return id ? desdeConfiguracion(configuracion) : duplicarConfiguracion(configuracion);
  }, [configuracion, id, idCargar]);

  // Se evalúa al abrir: un evento finalizado se consulta; uno en curso o con
  // ventas conserva su inicio, sus ventas y los productos vendidos.
  const soloLectura = Boolean(id && configuracion && eventoFinalizado(configuracion.evento));
  const restricciones = useMemo(() => (!id || !configuracion ? {} : {
    inicioBloqueado: eventoComenzado(configuracion.evento) || Boolean(configuracion.tieneVentas),
    ultimaVenta: configuracion.ultimaVentaArgentina || null,
    idsConVentas: (configuracion.idsProductosConVentas || []).map(String),
    // Ya comenzado: el tope de duración se cuenta desde ahora (o el fin guardado).
    comenzado: eventoComenzado(configuracion.evento),
    finGuardado: configuracion.evento.finArgentina || null,
  }), [configuracion, id]);

  const nombre = configuracion?.evento?.nombre || "";
  const titulo = id ? `${soloLectura ? "Ver" : "Editar"} «${nombre}»` : idDuplicar ? "Duplicar evento" : "Nuevo evento";
  const error = config.error || opciones.error || categorias.error;
  const volverAlListado = <SoftButton size="small" color="error" variant="text" onClick={() => navigate(RUTA_LISTADO)}>Volver al listado</SoftButton>;

  return (
    <DashboardLayout>
      <DashboardNavbar title={id ? (soloLectura ? "Ver evento" : "Editar evento") : idDuplicar ? "Duplicar evento" : "Nuevo evento"} />
      <SoftBox py={3}>
        {!autorizado ? <Alert severity="error">No tiene permiso para configurar eventos.</Alert>
          : error ? <Alert severity="error" action={volverAlListado}>{mensajeErrorEvento(error)}</Alert>
            : config.loading || opciones.loading || categorias.loading ? <Card><Loading /></Card>
              : !inicial ? <Alert severity="error" action={volverAlListado}>El evento no existe o fue eliminado.</Alert>
              : (
                // La versión en la clave rearma el asistente al recargar tras una edición concurrente.
                <Asistente key={`${id || "nuevo"}-${idDuplicar || ""}-${configuracion?.evento?.updatedAt || ""}`}
                  inicial={inicial} titulo={titulo} soloLectura={soloLectura} restricciones={restricciones}
                  onRecargar={() => config.refetch()}
                  opciones={opciones.data?.opcionesConfiguracionEventoComedor}
                  categorias={categorias.data?.categoriasProducto || []} />
              )}
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}
